import { useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';

import { captureMessage } from '../../logger/Logger';
import { bankAccountDisplayDetails } from '../state/SpacesAtoms';
import type { BankAccountDisplayDetail } from '../types/BankAccount';
import useGetAllBankAccountDetails from './useGetAllBankAccountDetails';

interface GetAccountDisplayDetailsFromGlobalStateDto {
    syncteraAccountId?: string;
    bankAccountId?: string;
}

const useGetAccountDisplayDetailsFromGlobalState = (
    {
        syncteraAccountId,
        bankAccountId,
    }: GetAccountDisplayDetailsFromGlobalStateDto,
    enabled = true,
) => {
    const [account, setAccount] = useState<BankAccountDisplayDetail | null>(
        null,
    );

    const accountDisplayDetails = useAtomValue(bankAccountDisplayDetails);

    const { bankAccountDetails, refetchAccountDetails, isLoading } =
        useGetAllBankAccountDetails(false);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        const account = accountDisplayDetails.find((acc) => {
            return (
                /*
                 * Doing null checks here because if someone passes in bankAccountId and not syncteraAccountId
                 * this will inadvertently match on the first account that doesn't have a charged secured account id
                 */
                (syncteraAccountId != null &&
                    acc.chargeSecuredSyncteraAccountId === syncteraAccountId) ||
                (syncteraAccountId != null &&
                    acc.syncteraAccountId === syncteraAccountId) ||
                (bankAccountId != null && acc.bankAccountId === bankAccountId)
            );
        });

        if (account) {
            setAccount((prevAccount) => {
                if (JSON.stringify(prevAccount) !== JSON.stringify(account)) {
                    return account;
                }
                return prevAccount;
            });
        } else {
            try {
                void refetchAccountDetails();
            } catch {
                captureMessage(
                    'Unable to get account details from global state',
                );
            }
            // If we attempt to use expo router here, we see "attempted to navigate before root component has mounted" error - handle fallback navigation in components.
        }
    }, [
        enabled,
        syncteraAccountId,
        bankAccountId,
        JSON.stringify(accountDisplayDetails),
        JSON.stringify(bankAccountDetails),
    ]);

    if (!enabled) {
        return { account: null, isLoading: false };
    }

    return { account, isLoading };
};

export default useGetAccountDisplayDetailsFromGlobalState;
